import React, { useState, Fragment } from 'react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Encounter, Patient, LabRequest } from '../../types';
import { FlaskConical, CheckCircle, Printer } from 'lucide-react';
import { fbcStructure, urinalysisTests, testosteroneTests } from '../../store/labStructures';
export const LaboratoryDashboard: React.FC = () => {
  const {
    encounters,
    patients,
    updateEncounter,
    currentUser
  } = useData();
  const [selectedRequest, setSelectedRequest] = useState<{
    enc: Encounter;
    lab: LabRequest;
  } | null>(null);
  const [results, setResults] = useState<Record<string, string>>({});
  // Find encounters with pending lab requests
  const pendingRequests = encounters.flatMap(enc => enc.labRequests.filter(lab => lab.status === 'Pending').map(lab => ({
    enc,
    lab
  })));
  const getPatient = (patientId: string) => patients.find(p => p.id === patientId);
  const handleSelectRequest = (item: {
    enc: Encounter;
    lab: LabRequest;
  }) => {
    setSelectedRequest(item);
    // Start from any previously-saved results so values persist when reopening
    const initialResults: Record<string, string> = {
      ...(item.lab.results || {})
    };
    const hasFBC = item.lab.tests.includes('Full Blood Count') || item.lab.tests.includes('FBC');
    const hasUrinalysis = item.lab.tests.some(t => t.toLowerCase().includes('urinalysis'));
    const hasTestosterone = item.lab.tests.some(t => t.toLowerCase().includes('testosterone'));
    // Only initialize empty strings for keys that don't already have a saved value
    const ensureKey = (k: string) => {
      if (initialResults[k] === undefined) initialResults[k] = '';
    };
    item.lab.tests.forEach(test => {
      if ((test === 'Full Blood Count' || test === 'FBC') && hasFBC) {
        fbcStructure.forEach(cat => {
          cat.tests.forEach(t => ensureKey(t.name));
        });
      } else if (test.toLowerCase().includes('urinalysis') && hasUrinalysis) {
        urinalysisTests.forEach(t => ensureKey(`UA_${t.name}`));
      } else if (test.toLowerCase().includes('testosterone') && hasTestosterone) {
        testosteroneTests.forEach(t => ensureKey(`TESTO_${t.name}`));
      } else {
        ensureKey(test);
      }
    });
    if (item.lab.otherTests) ensureKey('Others');
    setResults(initialResults);
  };
  const handleSaveResults = () => {
    if (!selectedRequest || !currentUser) return;
    const {
      enc,
      lab
    } = selectedRequest;
    const updatedLab: LabRequest = {
      ...lab,
      status: 'Completed',
      results,
      completedAt: new Date().toISOString(),
      completedBy: currentUser.id
    };
    const updatedLabRequests = enc.labRequests.map(l => l.id === lab.id ? updatedLab : l);
    // Check if all labs are completed
    const allLabsCompleted = updatedLabRequests.every(l => l.status === 'Completed');
    const updateData: Partial<Encounter> = {
      labRequests: updatedLabRequests,
      pendingLab: !allLabsCompleted
    };
    // If all labs done and no pending radiology, move back to clinician
    if (allLabsCompleted && !enc.pendingRadiology) {
      updateData.stage = 'CLINICIAN';
    }
    updateEncounter(enc.id, updateData);
    alert('Results saved and sent to Clinician.');
    setSelectedRequest(null);
  };
  const handlePrint = () => {
    window.print();
  };
  if (selectedRequest) {
    const patient = getPatient(selectedRequest.enc.patientId);
    if (!patient) return <div data-id="element-1120">Patient not found</div>;
    const hasFBC = selectedRequest.lab.tests.includes('Full Blood Count') || selectedRequest.lab.tests.includes('FBC');
    const hasUrinalysis = selectedRequest.lab.tests.some(t => t.toLowerCase().includes('urinalysis'));
    const hasTestosterone = selectedRequest.lab.tests.some(t => t.toLowerCase().includes('testosterone'));
    const otherTests = selectedRequest.lab.tests.filter(t => t !== 'Full Blood Count' && t !== 'FBC' && !t.toLowerCase().includes('urinalysis') && !t.toLowerCase().includes('testosterone'));
    return <div className="space-y-6 pb-20" data-id="element-1121">
        <div className="flex justify-between items-center print:hidden sticky top-0 bg-gray-50 z-10 py-4 border-b border-gray-200" data-id="element-1122">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-1123">
            Enter Laboratory Results
          </h1>
          <div className="flex space-x-2" data-id="element-1124">
            <Button variant="outline" onClick={() => setSelectedRequest(null)} data-id="element-1125">
              Back to Queue
            </Button>
            <Button variant="outline" onClick={handlePrint} data-id="element-1126">
              <Printer className="w-4 h-4 mr-2" data-id="element-1127" /> Print Results
            </Button>
            <Button onClick={handleSaveResults} className="bg-yellow-600 hover:bg-yellow-700 text-white" data-id="element-1128">
              <CheckCircle className="w-5 h-5 mr-2" data-id="element-1129" /> Save & Send to Clinician
            </Button>
          </div>
        </div>

        {/* Printable Area */}
        <div className="bg-white border border-gray-200 shadow-sm rounded-lg p-8 print:border-none print:shadow-none print:p-0" data-id="element-1130">
          {/* Print Header */}
          <div className="flex justify-between items-start mb-8 border-b-2 border-black pb-6" data-id="element-1131">
            <div className="flex items-center" data-id="element-1132">
              <img src="https://cdn.magicpatterns.com/uploads/3HmLapdTMRvTJbmSkdh1g8/Bmedhospital.png" alt="BMED Medical Centre" className="h-24 w-24 object-contain mr-4" data-id="element-1133" />
              <div data-id="element-1134">
                <h1 className="text-3xl font-bold text-pink-600 uppercase tracking-wider" data-id="element-1135">
                  BMED CARE
                </h1>
                <p className="text-sm italic text-gray-600" data-id="element-1136">
                  Maternity Care Made Better
                </p>
              </div>
            </div>
            <div className="text-right" data-id="element-1137">
              <p className="font-bold text-xl uppercase tracking-widest border-b-2 border-black pb-1 mb-2" data-id="element-1138">
                LABORATORY REPORT
              </p>
              <p className="text-sm" data-id="element-1139">
                <span className="font-semibold" data-id="element-1140">Date:</span>{' '}
                {new Date().toLocaleDateString()}
              </p>
              <p className="text-sm" data-id="element-1141">
                <span className="font-semibold" data-id="element-1142">File No:</span>{' '}
                {patient.bmcFileNumber}
              </p>
            </div>
          </div>

          {/* Patient Details */}
          <div className="border-2 border-black p-4 mb-8 text-sm space-y-3" data-id="element-1143">
            <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-1144">
              <div className="col-span-8" data-id="element-1145">
                <span className="font-semibold" data-id="element-1146">Patient Name:</span>{' '}
                {patient.firstName} {patient.lastName}
              </div>
              <div className="col-span-2" data-id="element-1147">
                <span className="font-semibold" data-id="element-1148">Age:</span> {patient.age}
              </div>
              <div className="col-span-2" data-id="element-1149">
                <span className="font-semibold" data-id="element-1150">Sex:</span> {patient.sex}
              </div>
            </div>
            <div className="grid grid-cols-12 gap-2" data-id="element-1151">
              <div className="col-span-6" data-id="element-1152">
                <span className="font-semibold" data-id="element-1153">Requested By:</span>{' '}
                {selectedRequest.enc.lastAttendingClinician}
              </div>
              <div className="col-span-6" data-id="element-1154">
                <span className="font-semibold" data-id="element-1155">Requested On:</span>{' '}
                {new Date(selectedRequest.lab.requestedAt).toLocaleString()}
              </div>
            </div>
          </div>

          {/* Results Section */}
          <div className="space-y-8" data-id="element-1156">
            {hasFBC && <div data-id="element-1157">
                <h3 className="font-bold text-lg mb-2 uppercase border-b border-gray-300 pb-1" data-id="element-1158">
                  Full Blood Count + Differential (FBC + DC)
                </h3>
                <table className="w-full border-collapse text-sm" data-id="element-1159">
                  <thead data-id="element-1160">
                    <tr className="bg-gray-100 border-y-2 border-black" data-id="element-1161">
                      <th className="p-2 text-left font-semibold" data-id="element-1162">
                        Test Description
                      </th>
                      <th className="p-2 text-left font-semibold w-48" data-id="element-1163">
                        Value(s)
                      </th>
                      <th className="p-2 text-left font-semibold" data-id="element-1164">
                        Reference range
                      </th>
                      <th className="p-2 text-left font-semibold" data-id="element-1165">Units</th>
                    </tr>
                  </thead>
                  <tbody data-id="element-1166">
                    {fbcStructure.map(category => <Fragment key={category.category} data-id="element-1167">
                        <tr data-id="element-1168">
                          <td colSpan={4} className="p-2 font-bold bg-gray-50 uppercase pt-4" data-id="element-1169">
                            {category.category}
                          </td>
                        </tr>
                        {category.tests.map(test => <tr key={test.name} className="border-b border-gray-100" data-id="element-1170">
                            <td className="p-2 pl-4" data-id="element-1171">{test.name}</td>
                            <td className="p-2" data-id="element-1172">
                              <input type="text" className="w-full p-1 border border-gray-300 rounded focus:ring-2 focus:ring-yellow-500 focus:outline-none print:border-none print:bg-transparent print:p-0 font-medium" value={results[test.name] || ''} onChange={e => setResults({
                        ...results,
                        [test.name]: e.target.value
                      })} placeholder="Enter value" data-id="element-1173" />
                            </td>
                            <td className="p-2 text-gray-600" data-id="element-1174">{test.range}</td>
                            <td className="p-2 text-gray-600" data-id="element-1175">{test.unit}</td>
                          </tr>)}
                      </Fragment>)}
                  </tbody>
                </table>
              </div>}

            {hasUrinalysis && <div data-id="element-1176">
                <h3 className="font-bold text-lg mb-3 uppercase text-center border-b-2 border-black pb-1" data-id="element-1177">
                  Urinalysis Report
                </h3>
                <div className="max-w-md mx-auto space-y-2 py-4" data-id="element-1178">
                  {urinalysisTests.map(test => <div key={test.name} className="flex items-center" data-id="element-1179">
                      <span className="w-16 font-bold text-sm" data-id="element-1180">
                        {test.name}
                      </span>
                      <span className="mx-3 text-gray-400" data-id="element-1181">-</span>
                      <input type="text" className="flex-1 p-1 border border-gray-300 rounded focus:ring-2 focus:ring-yellow-500 focus:outline-none print:border-none print:bg-transparent print:p-0 font-medium text-sm" value={results[`UA_${test.name}`] || ''} onChange={e => setResults({
                  ...results,
                  [`UA_${test.name}`]: e.target.value
                })} placeholder="Enter result" data-id="element-1182" />
                    </div>)}
                </div>
                <div className="max-w-md mx-auto mt-8 grid grid-cols-2 gap-4 text-sm" data-id="element-1183">
                  <div data-id="element-1184">
                    <label className="block font-semibold mb-1" data-id="element-1185">
                      Dr.'s Name:
                    </label>
                    <input type="text" className="w-full p-1 border-b border-black focus:outline-none print:border-black" value={results['UA_DR_NAME'] || ''} onChange={e => setResults({
                  ...results,
                  ['UA_DR_NAME']: e.target.value
                })} data-id="element-1186" />
                  </div>
                  <div data-id="element-1187">
                    <label className="block font-semibold mb-1" data-id="element-1188">
                      Signature:
                    </label>
                    <input type="text" className="w-full p-1 border-b border-black focus:outline-none print:border-black" value={results['UA_DR_SIG'] || ''} onChange={e => setResults({
                  ...results,
                  ['UA_DR_SIG']: e.target.value
                })} placeholder="Initials / signature" data-id="element-1189" />
                  </div>
                </div>
              </div>}

            {hasTestosterone && <div data-id="element-1190">
                <h3 className="font-bold text-lg mb-2 uppercase border-b border-gray-300 pb-1" data-id="element-1191">
                  Testosterone
                </h3>
                <table className="w-full border-collapse text-sm" data-id="element-1192">
                  <thead data-id="element-1193">
                    <tr className="bg-gray-100 border-y-2 border-black" data-id="element-1194">
                      <th className="p-2 text-left font-semibold" data-id="element-1195">
                        Test Description
                      </th>
                      <th className="p-2 text-left font-semibold w-48" data-id="element-1196">
                        Value(s)
                      </th>
                      <th className="p-2 text-left font-semibold" data-id="element-1197">
                        Reference Range
                      </th>
                      <th className="p-2 text-left font-semibold" data-id="element-1198">Units</th>
                    </tr>
                  </thead>
                  <tbody data-id="element-1199">
                    {testosteroneTests.map(test => <Fragment key={test.name} data-id="element-1200">
                        <tr className="border-b border-gray-100" data-id="element-1201">
                          <td className="p-2 font-medium" data-id="element-1202">{test.name}</td>
                          <td className="p-2" data-id="element-1203">
                            <input type="text" className="w-full p-1 border border-gray-300 rounded focus:ring-2 focus:ring-yellow-500 focus:outline-none print:border-none print:bg-transparent print:p-0 font-medium" value={results[`TESTO_${test.name}`] || ''} onChange={e => setResults({
                        ...results,
                        [`TESTO_${test.name}`]: e.target.value
                      })} placeholder="Enter value" data-id="element-1204" />
                          </td>
                          <td className="p-2 text-gray-600" data-id="element-1205">
                            <div data-id="element-1206">Males: {test.rangeMale}</div>
                            <div data-id="element-1207">Females: {test.rangeFemale}</div>
                          </td>
                          <td className="p-2 text-gray-600" data-id="element-1208">{test.unit}</td>
                        </tr>
                        <tr className="border-b border-gray-200 bg-gray-50" data-id="element-1209">
                          <td colSpan={4} className="p-2 text-xs text-gray-500 italic" data-id="element-1210">
                            Method: {test.method}
                          </td>
                        </tr>
                      </Fragment>)}
                  </tbody>
                </table>
              </div>}

            {(otherTests.length > 0 || selectedRequest.lab.otherTests) && <div data-id="element-1211">
                <h3 className="font-bold text-lg mb-2 uppercase border-b border-gray-300 pb-1" data-id="element-1212">
                  Other Investigations
                </h3>
                <table className="w-full border-collapse text-sm" data-id="element-1213">
                  <thead data-id="element-1214">
                    <tr className="bg-gray-100 border-y-2 border-black" data-id="element-1215">
                      <th className="p-2 text-left font-semibold w-1/2" data-id="element-1216">
                        Test Description
                      </th>
                      <th className="p-2 text-left font-semibold w-1/2" data-id="element-1217">
                        Result
                      </th>
                    </tr>
                  </thead>
                  <tbody data-id="element-1218">
                    {otherTests.map(test => <tr key={test} className="border-b border-gray-100" data-id="element-1219">
                        <td className="p-2 font-medium" data-id="element-1220">{test}</td>
                        <td className="p-2" data-id="element-1221">
                          <input type="text" className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-yellow-500 focus:outline-none print:border-none print:bg-transparent print:p-0 font-medium" value={results[test] || ''} onChange={e => setResults({
                      ...results,
                      [test]: e.target.value
                    })} placeholder="Enter result..." data-id="element-1222" />
                        </td>
                      </tr>)}
                    {selectedRequest.lab.otherTests && <tr className="border-b border-gray-100" data-id="element-1223">
                        <td className="p-2 font-medium" data-id="element-1224">
                          Others: {selectedRequest.lab.otherTests}
                        </td>
                        <td className="p-2" data-id="element-1225">
                          <input type="text" className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-yellow-500 focus:outline-none print:border-none print:bg-transparent print:p-0 font-medium" value={results['Others'] || ''} onChange={e => setResults({
                      ...results,
                      ['Others']: e.target.value
                    })} placeholder="Enter result..." data-id="element-1226" />
                        </td>
                      </tr>}
                  </tbody>
                </table>
              </div>}
          </div>

          {/* Footer Signatures */}
          <div className="mt-16 pt-8 border-t border-gray-300" data-id="element-1227">
            <div className="flex justify-between items-end" data-id="element-1228">
              <div className="w-64" data-id="element-1229">
                <p className="text-sm font-semibold mb-8" data-id="element-1230">
                  Laboratory Technologist:
                </p>
                <div className="border-b border-black" data-id="element-1231"></div>
                <p className="text-sm mt-2 font-medium" data-id="element-1232">
                  {currentUser?.fullName}
                </p>
                <p className="text-xs text-gray-500 mt-1" data-id="element-1233">
                  BMED Laboratory Dept
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>;
  }
  return <div className="space-y-6 print:hidden" data-id="element-1234">
      <div className="flex justify-between items-center" data-id="element-1235">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-1236">
          Laboratory Dashboard
        </h1>
        <Badge variant="warning" className="text-sm px-3 py-1" data-id="element-1237">
          {pendingRequests.length} Pending Requests
        </Badge>
      </div>

      <Card noPadding data-id="element-1238">
        <ul className="divide-y divide-gray-200" data-id="element-1239">
          {pendingRequests.length > 0 ? pendingRequests.map(item => {
          const patient = getPatient(item.enc.patientId);
          if (!patient) return null;
          return <li key={item.lab.id} className="p-4 hover:bg-gray-50 flex justify-between items-center" data-id="element-1240">
                  <div className="flex items-center" data-id="element-1241">
                    <div className="bg-yellow-100 p-2 rounded-full mr-4" data-id="element-1242">
                      <FlaskConical className="h-6 w-6 text-yellow-600" data-id="element-1243" />
                    </div>
                    <div data-id="element-1244">
                      <p className="text-sm font-medium text-blue-600" data-id="element-1245">
                        {patient.bmcFileNumber}
                      </p>
                      <p className="text-lg font-bold text-gray-900" data-id="element-1246">
                        {patient.firstName} {patient.lastName}
                      </p>
                      <p className="text-sm text-gray-500" data-id="element-1247">
                        Tests: {item.lab.tests.length} requested
                      </p>
                    </div>
                  </div>
                  <Button onClick={() => handleSelectRequest(item)} className="bg-yellow-600 hover:bg-yellow-700 text-white" data-id="element-1248">
                    Enter Results
                  </Button>
                </li>;
        }) : <li className="p-12 text-center text-gray-500" data-id="element-1249">
              <FlaskConical className="mx-auto h-12 w-12 text-gray-300 mb-4" data-id="element-1250" />
              <p className="text-lg font-medium text-gray-900" data-id="element-1251">
                No pending requests
              </p>
            </li>}
        </ul>
      </Card>
    </div>;
};